// Captação anônima first-party do módulo Crescimento (piloto, company_id 7).
// Nunca pode quebrar a página pública — todo erro é engolido silenciosamente.
// visitor_id/session_id são identificadores técnicos pseudônimos, isolados
// por empresa (a chave de storage inclui o slug — nunca uma id global que
// acompanharia a mesma pessoa entre empresas diferentes).

const API_BASE = process.env.REACT_APP_API_URL || 'https://app.agendali.com.br/api';

function genId(): string {
  try {
    if ((crypto as any)?.randomUUID) return (crypto as any).randomUUID();
  } catch {}
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

// event_id precisa caber em 64 chars (coluna do banco + validação do
// backend) — diferente de visitor_id/session_id (UUID puro, 36 chars, cabe
// tranquilo), o event_id combina tipo+sessão+sufixo então usa versões
// encurtadas de cada parte.
function genEventId(eventType: string, sessionId: string): string {
  const shortSession = sessionId.replace(/-/g, '').slice(0, 12);
  const shortRandom = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  return `${eventType}-${shortSession}-${shortRandom}`;
}

function safeStorageGet(storage: Storage, key: string): string | null {
  try { return storage.getItem(key); } catch { return null; }
}
function safeStorageSet(storage: Storage, key: string, value: string) {
  try { storage.setItem(key, value); } catch {}
}

export function getVisitorId(companySlug: string): string {
  const key = `growth_visitor_${companySlug}`;
  let id = safeStorageGet(window.localStorage, key);
  if (!id) { id = genId(); safeStorageSet(window.localStorage, key, id); }
  return id;
}

export function getSessionId(companySlug: string): string {
  const key = `growth_session_${companySlug}`;
  let id = safeStorageGet(window.sessionStorage, key);
  if (!id) { id = genId(); safeStorageSet(window.sessionStorage, key, id); }
  return id;
}

// Identificadores de sessão pra correlacionar com o booking_created
// autoritativo (ver GrowthController + PublicController.createBooking).
export function getGrowthIdsForBooking(companySlug: string): { visitor_id: string; session_id: string } {
  return { visitor_id: getVisitorId(companySlug), session_id: getSessionId(companySlug) };
}

function getUtmAndClickSignals() {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      // Só a presença é enviada — o valor bruto do click ID nunca sai do navegador.
      has_fbclid: params.has('fbclid'),
      has_gclid: params.has('gclid'),
      has_ttclid: params.has('ttclid'),
    };
  } catch {
    return {};
  }
}

const sentThisPageLoad = new Set<string>();

/**
 * Dispara um evento de captação. `growthEnabled` deve vir do payload público
 * da empresa (`company.growth_module_enabled`) — evita round-trip pra quem
 * não está no piloto, mas não é o único controle: o endpoint recusa gravar
 * de qualquer forma se a empresa não estiver habilitada.
 */
export function trackGrowthEvent(
  companySlug: string | undefined,
  growthEnabled: boolean | undefined,
  eventType: string,
  extra?: Record<string, unknown>
) {
  try {
    if (!companySlug || !growthEnabled) return;

    const sessionId = getSessionId(companySlug);
    const dedupKey = `${eventType}:${JSON.stringify(extra || {})}`;
    // Evita reenviar o mesmo evento várias vezes na mesma renderização
    // (ex.: efeito disparando de novo por re-render) — não é a idempotência
    // real (isso é o event_id único, verificado no backend), só uma
    // economia de rede no cliente.
    if (sentThisPageLoad.has(dedupKey)) return;
    sentThisPageLoad.add(dedupKey);

    const payload = {
      event_type: eventType,
      event_id: genEventId(eventType, sessionId),
      visitor_id: getVisitorId(companySlug),
      session_id: sessionId,
      referrer: document.referrer || undefined,
      ...getUtmAndClickSignals(),
      ...extra,
    };

    const url = `${API_BASE}/public/${companySlug}/growth/events`;
    const body = JSON.stringify(payload);

    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  } catch {
    // Telemetria nunca pode quebrar a página pública.
  }
}
